import type { DefaultError, UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { get } from './http';
import { QueryKeys } from './query-keys';

type PollTransactionStatusParams = {
  txid?: string;
  esploraUrl: string | null;
  onConfirmed?: (txInfo: { txid: string }) => void;
};

type PollTransactionStatusResponse = {
  txid: string;
  status: {
    confirmed: boolean;
  };
};

type QueryOptionsParam = Omit<UseQueryOptions<PollTransactionStatusResponse, DefaultError>, 'queryKey' | 'queryFn'>;

export async function fetchTransactionStatus(esploraUrl: string, txid: string, signal?: AbortSignal) {
  const path = `${esploraUrl}/tx/${txid}`;
  return get<PollTransactionStatusResponse>(path, undefined, { signal });
}

export function usePollTransactionStatus(params: PollTransactionStatusParams, options?: QueryOptionsParam) {
  const { txid, esploraUrl, onConfirmed } = params;

  return useQuery({
    queryKey: [QueryKeys.transaction, txid],
    queryFn: ({ signal }) => fetchTransactionStatus(params.esploraUrl || '', txid || '', signal),
    refetchInterval: (query) => {
      const data = query.state.data;
      const isConfirmed = data?.status.confirmed;

      if (onConfirmed && data && isConfirmed) {
        onConfirmed({
          txid: data.txid,
        });
      }

      return isConfirmed ? false : 5000;
    },
    enabled: !!txid && !!esploraUrl,
    ...options,
  });
}
